/**
 * PlanItemRow — one ranked priority on The Plan page.
 *
 * Tappable (calls onSelect with the item). Below-the-cut rows render a
 * `reason` line (see ../lib/cutReasons.ts) instead of the hover affordance
 * above-cut rows get, since there is nothing to compare them against.
 */

import type { PlanItemViewModel } from '../lib/planViewModel';
import { RankMark } from '@/components/prioritization/RankMark';

interface PlanItemRowProps {
  item: PlanItemViewModel;
  /** Present only for below-the-cut rows — see lib/cutReasons.ts. */
  reason?: string;
  onSelect: (item: PlanItemViewModel) => void;
}

export function PlanItemRow({ item, reason, onSelect }: PlanItemRowProps) {
  const aboveCut = item.aboveCutLine;

  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={`w-full text-left flex items-start gap-3 px-4 py-3  border transition-colors ${
        aboveCut
          ? 'bg-[var(--surface)] border-[var(--rule)] hover:border-[var(--rule-bright)] hover:bg-[var(--hover-tint)]'
          : 'bg-[var(--bg)] border-[var(--rule)] opacity-80 hover:opacity-100'
      }`}
    >
      {/* A ratified cycle report's order: agreed, not of record until a Plan of Record is authorized */}
      <RankMark rank={item.rank} official={false} aboveTheLine={aboveCut} />
      <span className="min-w-0 flex-1">
        <span
          className={`block text-sm font-medium ${
            aboveCut ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'
          }`}
        >
          {item.title}
        </span>
        {reason && (
          <span className="block text-xs text-[var(--text-ter)] mt-0.5">{reason}</span>
        )}
      </span>
    </button>
  );
}
